import CalendarEventsFilterInfo from "../../context/calendarEventsFilterInfo";
import DeliveriesFilterInfo from "../../context/deliveriesFilterinfo";
import Helper from "../../Helper";
import { Delivery } from "../../models/Delivery";
import { plotFetch } from "../Helper/ApiRequest";

export function getProjectDeliveries(
    teamSubdomain: string,
    project_token: string,
    filterInfo: DeliveriesFilterInfo,
    timezone: string
): Promise<Delivery[]> {
    const url = new URL(`${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${project_token}/deliveries`);
    const searchParams = filterInfo.toUrlSearchParams();

    url.search = searchParams.toString();

    return plotFetch(url.toString(), { method: "GET" })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            return [];
        })
        .then((response) => {
            return response.map((delivery: any) => Delivery.fromApiObject(delivery, timezone));
        })
        .catch((error) => {
            return [];
        });
}

export function createDelivery(
    teamSubdomain: string,
    project_token: string,
    delivery: Delivery,
    is_repeating?: boolean
): Promise<Delivery> {
    const url = new URL(`${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${project_token}/deliveries`);

    if (is_repeating) {
        url.searchParams.append("repeating", "true");
    }

    return plotFetch(
        url.toString(),
        {
            method: "post",
            body: JSON.stringify({ delivery: delivery.toApiObject() }),
        },
        true
    )
        .then((data) => {
            if (data.ok) {
                return data.json();
            }
            throw new Error("Network error.");
        })
        .then((data) => {
            return data;
        })
        .catch(() => {
            return null;
        });
}

export function updateDelivery(
    teamSubdomain: string,
    project_token: string,
    delivery: Delivery
): Promise<Delivery | null> {
    return plotFetch(
        `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${project_token}/deliveries/${delivery.id}`,
        {
            method: "put",
            body: JSON.stringify({ delivery: delivery.toApiObject() }),
        },
        true
    )
        .then((data) => {
            if (data.ok) {
                return data.json();
            }
            throw new Error("Network error.");
        })
        .then((data) => {
            return data;
        })
        .catch(() => {
            return null;
        });
}

export function deleteDelivery(teamSubdomain: string, projectToken: string, delivery: Delivery): Promise<boolean> {
    const url = `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/deliveries/${delivery.id}`;

    const options = {
        method: "delete",
    };

    return Helper.plotFetch(url, options, true)
        .then((data) => {
            if (data.ok) {
                return data.json();
            }

            throw new Error("Network error.");
        })
        .then((data) => {
            return data;
        })
        .catch(() => {
            return false;
        });
}

/**
 * Get a delivery by its project information and eithits unique token or id. Eventually we should remove the unique token and just use the id, but for now we need to support both.
 */
export function getDelivery(
    teamSubdomain: string,
    projectToken: string,
    deliveryId: number,
    timezone: string
): Promise<Delivery | null> {
    return plotFetch(`${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/deliveries/${deliveryId}`, {
        method: "GET",
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return Delivery.fromApiObject(response, timezone);
        })
        .catch(() => {
            return null;
        });
}

export function getDeliveryByUniqueToken(
    teamSubdomain: string,
    unique_token: string,
    timezone: string
): Promise<Delivery | null> {
    const url = `${Helper.baseUrlAltSubdomain(teamSubdomain)}/deliveries/${unique_token}`;
    return plotFetch(url, { method: "GET" })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return Delivery.fromApiObject(response, timezone);
        })
        .catch(() => {
            return null;
        });
}

export const getProjectDeliveryFilters = (teamSubdomain: string, project_token: string): Promise<any[]> => {
    const url = `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${project_token}/delivery_filters`;
    return plotFetch(url, {
        method: "GET",
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return response;
        })
        .catch(() => {
            return [];
        });
};

export const createProjectDeliveryFilter = (
    teamSubdomain: string,
    project_token: string,
    filterInfo: CalendarEventsFilterInfo,
    name: string
): Promise<any | null> => {
    const url = `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${project_token}/delivery_filters`;
    return plotFetch(url, {
        method: "POST",
        body: JSON.stringify({
            delivery_filter: {
                name: name,
                filter: filterInfo.toApiObject(),
            },
        }),
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return response;
        })
        .catch(() => {
            return null;
        });
};

export const saveProjectDeliveryFilter = (
    teamSubdomain: string,
    project_token: string,
    delivery_filter_id: number,
    filterInfo: CalendarEventsFilterInfo,
    name: string
): Promise<any | null> => {
    const url = `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${project_token}/delivery_filters/${delivery_filter_id}`;
    return plotFetch(url, {
        method: "PUT",
        body: JSON.stringify({
            delivery_filter: {
                name: name,
                filter: filterInfo.toApiObject(),
            },
        }),
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return response;
        })
        .catch(() => {
            return null;
        });
};
export const deleteProjectDeliveryFilter = (
    teamSubdomain: string,
    project_token: string,
    delivery_filter_id: number
): Promise<boolean> => {
    const url = `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${project_token}/delivery_filters/${delivery_filter_id}`;
    return plotFetch(url, {
        method: "DELETE",
    })
        .then((response) => {
            return response.ok;
        })
        .catch(() => {
            return false;
        });
};

export const createDeliveryExport = (
    teamSubdomain: string,
    projectToken: string,
    filterInfo: CalendarEventsFilterInfo,
    exportFormat: "csv"
): Promise<Response> => {
    const url = `${Helper.baseUrlAltSubdomain(teamSubdomain)}/projects/${projectToken}/delivery_exports`;

    const data = filterInfo.toApiObject();

    data.export_format = exportFormat;

    return plotFetch(url.toString(), {
        method: "POST",
        body: JSON.stringify(data),
    });
};

export function sendOverviewEmail(project: any) {
    const url =
        Helper.baseUrlAltSubdomain(project.claimed_by_team_subdomain) +
        "/projects/" +
        project.unique_token +
        "/delivery_overview_email_send";

    const options = {
        method: "post",
    };

    return Helper.plotFetch(url, options, true)
        .then((data) => {
            if (data.ok) {
                return data.json();
            }
            throw new Error("Network error.");
        })
        .then((data) => {
            return data;
        })
        .catch(() => {
            return null;
        });
}
